import {
  Activity,
  ShieldCheck,
  ShieldAlert,
  Server,
  Sparkles,
  Users,
  Stethoscope,
  AlertTriangle,
  Wallet,
  FlaskConical,
  Package,
  Bug,
  Brain,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  type LucideIcon,
} from "lucide-react";

/* ----------------------------- KPI primitives ---------------------------- */

function TopKpi({
  label,
  value,
  hint,
  tone = "neutral",
  icon: Icon,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: "neutral" | "success" | "warning" | "danger";
  icon: LucideIcon;
}) {
  const toneRing =
    tone === "success"
      ? "ring-success/30"
      : tone === "warning"
        ? "ring-[oklch(0.78_0.16_85)]/30"
        : tone === "danger"
          ? "ring-accent/40"
          : "ring-white/10";
  return (
    <div className={`card-surface rounded-2xl p-5 ring-1 ${toneRing}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-3 text-4xl font-light tracking-tight">{value}</div>
        </div>
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/5 text-muted-foreground">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </div>
      </div>
      <div className="mt-3 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

function MiniKpi({
  label,
  value,
  delta,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  delta: string;
  tone: "success" | "warning" | "danger" | "neutral";
  icon: LucideIcon;
}) {
  const dotTone =
    tone === "success"
      ? "bg-success"
      : tone === "warning"
        ? "bg-[oklch(0.82_0.16_85)]"
        : tone === "danger"
          ? "bg-accent"
          : "bg-muted-foreground";
  return (
    <div className="card-surface rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-white/5 text-muted-foreground">
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <span className={`h-2 w-2 rounded-full ${dotTone}`} />
      </div>
      <div className="mt-3 text-2xl font-light">{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground line-clamp-1">
        {label}
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground/80">{delta}</div>
    </div>
  );
}

/* ---------------------------- Domain status card ------------------------- */

type DomainTone = "success" | "warning" | "danger";

function DomainStatus({
  title,
  scope,
  tone,
  icon: Icon,
  rows,
  cta,
}: {
  title: string;
  scope: string;
  tone: DomainTone;
  icon: LucideIcon;
  rows: { label: string; value: string; ok?: boolean }[];
  cta: string;
}) {
  const toneBg =
    tone === "success"
      ? "bg-success/15 text-success"
      : tone === "warning"
        ? "bg-[oklch(0.82_0.16_85)]/15 text-[oklch(0.85_0.15_85)]"
        : "bg-accent/15 text-accent";
  const toneLabel =
    tone === "success" ? "Stable" : tone === "warning" ? "À surveiller" : "Critique";
  return (
    <div className="card-surface flex h-full flex-col rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/5">
            <Icon className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium">{title}</div>
            <div className="truncate text-[11px] text-muted-foreground">{scope}</div>
          </div>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${toneBg}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {toneLabel}
        </span>
      </div>
      <ul className="mt-4 flex-1 space-y-2.5">
        {rows.map((r) => (
          <li
            key={r.label}
            className="flex items-center justify-between gap-3 text-xs"
          >
            <span className="truncate text-muted-foreground">{r.label}</span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              {r.ok ? (
                <CheckCircle2 className="h-3 w-3 text-success" />
              ) : (
                <Clock className="h-3 w-3 text-muted-foreground" />
              )}
              {r.value}
            </span>
          </li>
        ))}
      </ul>
      <button className="mt-5 inline-flex items-center justify-between gap-2 rounded-xl bg-white/5 px-3 py-2 text-xs hover:bg-white/10">
        <span>{cta}</span>
        <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ------------------------- Operations center pill ------------------------ */

function CenterCard({
  title,
  scope,
  icon: Icon,
  accent,
  onClick,
  badge,
}: {
  title: string;
  scope: string;
  icon: LucideIcon;
  accent: string;
  onClick?: () => void;
  badge?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="card-surface group relative flex h-full flex-col gap-3 rounded-2xl p-4 text-left transition hover:bg-white/[0.04]"
    >
      {badge && (
        <span
          className="absolute right-3 top-3 rounded-full bg-success/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-success"
        >
          {badge}
        </span>
      )}
      <div className="flex items-center justify-between">
        <div
          className="grid h-9 w-9 place-items-center rounded-lg"
          style={{ background: accent }}
        >
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </div>
        {!badge && <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />}
      </div>
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-1 text-[11px] text-muted-foreground line-clamp-2">
          {scope}
        </div>
      </div>
    </button>
  );
}


/* ---------------------------------- View --------------------------------- */

export default function SuperAdminView({ onOpenCenter }: { onOpenCenter?: (id: string) => void } = {}) {
  return (
    <div className="flex flex-col gap-6">
      {/* Banner */}
      <div className="card-surface flex flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-success/15 text-success">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-medium">
              L'établissement tourne normalement.
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              1 incident mineur nécessite votre attention.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { l: "Capacité lits", v: "220" },
            { l: "Patients / jour", v: "138" },
            { l: "Actes critiques", v: "5" },
            { l: "SLA uptime", v: "99.92%" },
          ].map((k) => (
            <div
              key={k.l}
              className="rounded-xl bg-white/5 px-3 py-2 text-center"
            >
              <div className="text-base font-light">{k.v}</div>
              <div className="text-[10px] text-muted-foreground">{k.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* HERO: NEURO CENTER */}
      <button
        onClick={() => onOpenCenter?.("neuro-command-center")}
        className="card-surface group relative flex flex-col gap-4 overflow-hidden rounded-2xl p-6 text-left transition hover:bg-white/[0.04] lg:flex-row lg:items-center"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.18 0.08 300 / 0.35), oklch(0.18 0.06 200 / 0.25))",
          border: "1px solid oklch(0.65 0.2 300 / 0.4)",
        }}
      >
        <div className="grid h-14 w-14 place-items-center rounded-xl" style={{ background: "oklch(0.65 0.2 300 / 0.3)" }}>
          <Brain className="h-7 w-7" strokeWidth={1.6} style={{ color: "oklch(0.85 0.15 300)" }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success">
              NOUVEAU · v10.0
            </span>
            <span className="text-xs text-muted-foreground">EEG · Neurophysiologie · ICU</span>
          </div>
          <div className="mt-2 text-lg font-semibold">
            Neuro Command Center — Monitoring EEG en temps réel
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Cohérence inter-hémisphérique · PSD multitaper · wPLI/PLI/PLV · détection de crises · staging sommeil · réseau cérébral
          </div>
        </div>
        <div className="flex items-center gap-2 self-end lg:self-center">
          <span className="text-sm font-medium text-success">Ouvrir →</span>
        </div>
      </button>

      {/* TOP KPIs */}
      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
            Top KPIs
          </h2>
          <span className="text-[11px] text-muted-foreground">8 métriques</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
          <MiniKpi label="Patients aujourd'hui" value="138" delta="+6 vs hier" tone="success" icon={Users} />
          <MiniKpi label="Consultations actives" value="42" delta="12 en attente" tone="neutral" icon={Stethoscope} />
          <MiniKpi label="Alertes cliniques" value="4" delta="2 critiques" tone="danger" icon={AlertTriangle} />
          <MiniKpi label="Revenus CNSS (MAD)" value="86k" delta="+4.2% / sem" tone="success" icon={Wallet} />
          <MiniKpi label="Analyses labo en attente" value="17" delta="3 > 24h" tone="warning" icon={FlaskConical} />
          <MiniKpi label="Stocks critiques" value="6" delta="2 ruptures" tone="warning" icon={Package} />
          <MiniKpi label="Threats SOC (24h)" value="12" delta="1 actif" tone="danger" icon={Bug} />
          <MiniKpi label="AI suggestions ICD-10" value="47" delta="92% précision" tone="success" icon={Brain} />
        </div>
      </section>

      {/* DOMAIN STATUS */}
      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
            Domain Status
          </h2>
          <span className="text-[11px] text-muted-foreground">4 domaines</span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <DomainStatus
            title="Clinical Status"
            scope="Patients · Soins · Labo · Pharmacie"
            tone="success"
            icon={Stethoscope}
            rows={[
              { label: "Patients admis aujourd'hui", value: "24", ok: true },
              { label: "Lits occupés", value: "62 / 80", ok: true },
              { label: "Examens critiques en attente", value: "2" },
              { label: "Dispensation contrôlée", value: "OK", ok: true },
            ]}
            cta="Ouvrir · Traiter"
          />
          <DomainStatus
            title="Security Status"
            scope="SOC · Threats · IoMT · Audit"
            tone="warning"
            icon={ShieldAlert}
            rows={[
              { label: "Threats actives", value: "2" },
              { label: "Audit chain integrity", value: "Broken" },
              { label: "IoMT sensors online", value: "21 / 22", ok: true },
              { label: "Failed logins (1h)", value: "1" },
            ]}
            cta="Ouvrir · Observer"
          />
          <DomainStatus
            title="System Status"
            scope="API · Cache · DB · Compliance"
            tone="success"
            icon={Server}
            rows={[
              { label: "Database memory", value: "62%", ok: true },
              { label: "Upstash Vector", value: "OK", ok: true },
              { label: "Upstash Redis", value: "OK", ok: true },
              { label: "WebSocket clients", value: "3", ok: true },
            ]}
            cta="Ouvrir · Administrer"
          />
          <DomainStatus
            title="AI Status"
            scope="ICD-10 · Drug advisor · Predictions"
            tone="success"
            icon={Sparkles}
            rows={[
              { label: "ICD-10 suggestions (7j)", value: "417", ok: true },
              { label: "Drug interaction warnings", value: "3" },
              { label: "Predictive model version", value: "v1.4.2", ok: true },
              { label: "Vector index size", value: "18k", ok: true },
            ]}
            cta="Ouvrir · Augmenter"
          />
        </div>
      </section>

      {/* OPERATIONS CENTERS */}
      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
            Centres opérationnels
          </h2>
          <span className="text-[11px] text-muted-foreground">6 centres</span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <CenterCard
            title="Clinical Operations Center"
            scope="Patients · Cliniciens · Labo · Imagerie · Pharmacie · Facturation"
            icon={Stethoscope}
            accent="oklch(0.78 0.18 150 / 0.18)"
            onClick={() => onOpenCenter?.("clinicalops")}
          />
          <CenterCard
            title="Operations Center"
            scope="Lits · Personnel · Stocks · Équipements · Logistique"
            icon={Activity}
            accent="oklch(0.7 0.02 240 / 0.18)"
            onClick={() => onOpenCenter?.("operations")}
          />
          <CenterCard
            title="Security Operations Center"
            scope="Threats · Audit · IoMT · Réponse aux incidents"
            icon={ShieldAlert}
            accent="oklch(0.7 0.2 35 / 0.18)"
            onClick={() => onOpenCenter?.("security")}
          />
          <CenterCard
            title="Platform Governance Center"
            scope="IAM · Compliance · Politiques · PII"
            icon={Server}
            accent="oklch(0.82 0.16 85 / 0.18)"
            onClick={() => onOpenCenter?.("governance")}
          />
          <CenterCard
            title="AI Center"
            scope="ICD-10 · Drug interactions · Notes · Predictions"
            icon={Sparkles}
            accent="oklch(0.7 0.18 295 / 0.18)"
          />
          <CenterCard
            title="Neuro Command Center"
            scope="EEG · Coherence · Seizure · Sleep · ICU · Brain network"
            icon={Brain}
            accent="oklch(0.65 0.2 300 / 0.2)"
            onClick={() => onOpenCenter?.("neuro-command-center")}
            badge="NOUVEAU"
          />

        </div>
      </section>
    </div>
  );
}
